<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <div
    class="sam-sidebar__wrapper"
    data-samora
  >
    <transition name="fade">
      <div
        v-if="isOpen"
        class="sam-sidebar__overlay elevation--2"
        @click="$emit('close')"
      />
    </transition>
    <transition
      :name="slideAnimation"
      @before-enter="beforeSidebarOpen()"
      @enter="sidebarOpening()"
      @before-leave="beforeSidebarClose()"
      @after-leave="afterSidebarClose()"
    >
      <div
        v-if="isOpen"
        ref="sidebar"
        class="sam-sidebar"
        :class="sidebarClassObject"
        role="dialog"
        tabindex="0"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>
<script>
import { lockScroll, unlockScroll } from '../../../utils/scrollLock';

let previouslyFocusedElement;

export default {
  name: 'SamSidebar',
  props: {
    /**
     * If the sidebar is open or not
     *
     * @values true, false
     */
    isOpen: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Which side the sidebar opens from
     *
     * @values right, left
     */
    position: {
      type: String,
      required: false,
      default: 'right',
      validator(type) {
        return ['right', 'left'].includes(type);
      },
    },
    /** If the card should be compact (i.e., no outer padding)
     * @values true, false
     */
    compact: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    sidebarClassObject() {
      return {
        'is-right': this.position === 'right',
        'is-left': this.position === 'left',
        'not-compact': !this.compact,
      };
    },
    slideAnimation() {
      return this.position === 'right' ? 'slideFromRight' : 'slideFromLeft';
    },
  },
  mounted() {
    if (!this.isOpen) {
      return;
    }

    lockScroll();
    this.focusSidebar();
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  destroyed() {
    if (previouslyFocusedElement) {
      previouslyFocusedElement.focus();
    }

    unlockScroll();
  },
  methods: {
    beforeSidebarOpen() {
      previouslyFocusedElement = document.activeElement;
      lockScroll();
    },
    sidebarOpening() {
      this.focusSidebar();
      document.addEventListener('keydown', this.handleKeyDown);
    },
    beforeSidebarClose() {
      document.removeEventListener('keydown', this.handleKeyDown);

      if (previouslyFocusedElement) {
        previouslyFocusedElement.focus();
      }
    },
    afterSidebarClose() {
      unlockScroll();
    },
    /**
     * This method is incomplete and a very naive way of getting focusable elements.
     * Will do for now but as Unbabel grows we might need to use a more mature solution like:
     * https://github.com/focus-trap/tabbable
     */
    getFocusableElements() {
      const focusableQuery =
        'button:enabled:not(.is-icon), [href], input:enabled, select:enabled, textarea:enabled, [contenteditable], [tabindex]:not([tabindex="-1"]):not([disabled])';
      return Array.from(this.$refs.sidebar.querySelectorAll(focusableQuery));
    },
    focusSidebar() {
      const autofocusChild = this.$refs.sidebar.querySelector('[autofocus]');

      if (autofocusChild) {
        autofocusChild.focus();
        return;
      }

      const focusableElements = this.getFocusableElements();

      if (!focusableElements.length) {
        this.$refs.sidebar.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      firstFocusableElement.focus();
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        /**
         * Triggered when the user triggered the intent to close like the ESCAPE key or outside
         * of the sidebar area
         */
        this.$emit('close');
        return;
      }

      /**
       * Trap focus inside Sidebar
       * SHIFT + TAB - focus previous element
       * TAB - focus next element
       */
      if (event.key === 'Tab') {
        const focusableElements = this.getFocusableElements();

        /**
         * If there are no focusable elements, keep the focus on the sidebar div
         */
        if (!focusableElements.length) {
          event.preventDefault();
          this.$refs.sidebar.focus();
          return;
        }

        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];

        /**
         * If the current focus is the first focusable element and the user wants to go back
         * then let's focus on the last element
         */
        if (event.shiftKey && document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
          return;
        }

        /**
         * If the current focus is the last focusable element on the sidebar then let's focus
         * on the first
         */
        if (!event.shiftKey && document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.sam-sidebar__wrapper,
.sam-sidebar__wrapper > * {
  z-index: $elevation--4;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slideFromRight-enter-active,
.slideFromRight-leave-active {
  transform: unset;
  transition: transform 0.2s;
}

.slideFromRight-enter,
.slideFromRight-leave-to {
  transform: translateX(100%);
}

.slideFromLeft-enter-active,
.slideFromLeft-leave-active {
  transform: unset;
  transition: transform 0.2s;
}

.slideFromLeft-enter,
.slideFromLeft-leave-to {
  transform: translateX(-100%);
}

.sam-sidebar__overlay {
  cursor: pointer;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.8);
}

.sam-sidebar {
  @extend .d-flex, .flex-column, .elevation--3, .shadow--card;

  &.not-compact {
    @extend .py-8, .px-6;
  }

  position: fixed;
  top: 0;
  bottom: 0;
  background-color: $neutral--10;
  width: 50%;
  max-width: 712px;
  overflow-y: auto;
  overflow-x: hidden;

  &.is-left {
    border-right: $size--0--fourth solid $neutral--30;
    left: 0;
  }

  &.is-right {
    border-left: $size--0--fourth solid $neutral--30;
    right: 0;
  }

  @media only screen and (max-width: 48em) {
    width: 100%;
    max-width: 100%;
  }
}
</style>
